import React from 'react';
import { ShieldCheck, ShieldAlert, Sparkles, Loader2, ArrowDown } from 'lucide-react';

const TIPS = {
  name: 'Précisez le nom : type de pièce + marque + référence (ex. « Plaquettes de frein avant BREMBO P85020 »).',
  brandRef: 'Renseignez la marque et la référence fabricant : indispensables pour la recherche et les comparateurs.',
  title: 'Rédigez un titre SEO de 30 à 60 caractères contenant le mot-clé principal (ou générez-le par IA ci-dessous).',
  meta: 'Rédigez une méta description de 120 à 160 caractères avec un bénéfice concret (livraison 48 h, garantie 2 ans).',
  h1: 'Donnez un H1 lisible (10–70 car.), différent du titre SEO, avec le mot-clé principal.',
  kwIntro: 'Définissez le mot-clé principal et placez-le dans la première phrase de la description.',
  words: 'Développez la description (≥ 150 mots) : fonction, montage, symptômes d\'usure, compatibilités.',
  h2: 'Structurez la description en sections « ## » (Caractéristiques, Montage, Compatibilité, FAQ).',
  read: 'Raccourcissez les phrases (≤ 25 mots en moyenne) pour la lisibilité.',
  image: 'Ajoutez une image principale : Google Images et Shopping la réclament.',
  cat: 'Choisissez la catégorie et la sous-catégorie : la fiche sera reliée aux pages de catégories.',
  compat: 'Ajoutez au moins une compatibilité véhicule (ou marquez la pièce universelle) pour la recherche par plaque.',
  uniq: 'Reformulez les passages communs avec d\'autres fiches : visez ≥ 75 % de contenu unique.',
  langs: 'Traduisez nom et description en EN / DE / ES / IT (bouton « Améliorer par IA »).',
};
const AI_KEYS = new Set(['words', 'h2', 'read', 'langs', 'kwIntro', 'uniq']);

// Note SEO /100 + points à corriger avant publication (calculée sur le formulaire, avant enregistrement)
export const ProductPublishReadiness = ({ score, published, isNew, onImprove, improving }) => {
  const note = Math.round((score.points / score.max) * 100);
  const failing = score.checks.filter((c) => (c.weight ? c.got < c.weight : !c.ok));
  const level = note >= 80 ? 'good' : note >= 55 ? 'warn' : 'bad';
  const cls = { good: 'border-green-200 bg-green-50 text-green-800', warn: 'border-amber-200 bg-amber-50 text-amber-800', bad: 'border-red-200 bg-red-50 text-red-800' }[level];
  const bar = { good: 'bg-green-500', warn: 'bg-amber-500', bad: 'bg-red-500' }[level];
  const Icon = level === 'good' ? ShieldCheck : ShieldAlert;
  return (
    <div className={`md:col-span-2 border rounded-lg p-3 ${cls}`} data-testid="product-publish-readiness" data-note={note}>
      <div className="flex flex-wrap items-center gap-3">
        <Icon size={18} />
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-sm font-semibold" data-testid="product-seo-note">Note SEO : {note}/100 <span className="font-normal text-xs opacity-80">(grade {score.grade})</span></span>
            <span className="text-xs" data-testid="product-seo-verdict">{level === 'good' ? 'Prête à publier' : (published && !isNew) ? (level === 'bad' ? 'Publiée malgré une fiche faible — Google la classera mal' : 'Publiée, mais perfectible') : (level === 'bad' ? 'À compléter avant publication' : 'Presque prête')}</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/70 mt-1 overflow-hidden"><div className={`h-full ${bar} transition-[width] duration-500`} style={{ width: `${note}%` }} /></div>
        </div>
        {!isNew && failing.some((c) => AI_KEYS.has(c.key)) && (
          <button type="button" onClick={onImprove} disabled={improving} className="inline-flex items-center gap-1 text-xs font-medium text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-60 rounded-md px-2.5 py-1" data-testid="product-readiness-improve">
            {improving ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Corriger par IA
          </button>
        )}
      </div>
      {failing.length > 0 && (
        <ol className="mt-2 space-y-1 text-xs" data-testid="product-readiness-points">
          <li className="font-medium flex items-center gap-1"><ArrowDown size={11} /> {failing.length} point(s) à améliorer{!published || isNew ? ' avant publication' : ''} :</li>
          {failing.slice(0, 6).map((c) => (
            <li key={c.key} className="flex gap-1.5" data-testid={`product-readiness-point-${c.key}`}><span className="opacity-60">•</span><span><strong>{c.label}</strong> <span className="opacity-70">({c.hint})</span> — {TIPS[c.key]}</span></li>
          ))}
          {failing.length > 6 && <li className="opacity-70">… et {failing.length - 6} autre(s), voir la liste complète ci-dessous.</li>}
        </ol>
      )}
    </div>
  );
};

export default ProductPublishReadiness;
